import { API_ENDPOINTS } from './apiConfig';

const REGEOCODE_URL = API_ENDPOINTS.reverseGeocode;
const LOCATION_REQUEST_DEADLINE_MS = 10000;
const GEOLOCATION_OPTIONS = {
  enableHighAccuracy: true,
  timeout: 10000,
  maximumAge: 15000
};
const IOS_LOCATION_GUIDANCE = '网页无法直接打开系统设置。请前往“设置 → 隐私与安全性 → 定位服务 → Chrome”，选择“使用 App 期间”，并允许当前网站定位；返回本页后会自动重新定位。';

function isValidCoordinate (value) {
  return typeof value === 'number' && isFinite(value);
}

function normalizeCityName (value) {
  const city = Array.isArray(value) ? value[0] : value;
  return String(city || '').replace(/市$/, '');
}

function buildCityCode (adcode) {
  const code = String(adcode || '');
  return code.length >= 6 ? code.slice(0, -2) + '00' : code;
}

function getGbCityCode (component) {
  if (component && Object.prototype.hasOwnProperty.call(component, 'gbCityCode')) {
    return String(component.gbCityCode || '');
  }
  return buildCityCode(component && component.adcode);
}

function createCoordinateOnlyLocation (position, reason = 'reverse_geocode_failed') {
  return {
    acronym: '',
    firstChar: '',
    id: '',
    gbCityCode: '',
    name: '当前位置',
    pinyin: '',
    rank: '',
    latitude: position.latitude,
    longitude: position.longitude,
    coordinateSystem: 'wgs84',
    accuracyMeters: position.accuracyMeters ?? null,
    timestampMs: position.timestampMs ?? null,
    cityResolution: 'unavailable',
    cityResolutionReason: reason
  };
}

function createLocationError (message, reason, code = 0) {
  const error = new Error(message);
  error.locationReason = reason;
  if (code) {
    error.code = code;
  }
  return error;
}

function isIOSDevice (navigatorRef = typeof navigator === 'undefined' ? null : navigator) {
  if (!navigatorRef) {
    return false;
  }
  const userAgent = String(navigatorRef.userAgent || '');
  const platform = String(navigatorRef.platform || '');
  return /iPad|iPhone|iPod/i.test(userAgent) || (
    platform === 'MacIntel' && Number(navigatorRef.maxTouchPoints || 0) > 1
  );
}

function reverseGeocode (longitude, latitude, metadata = {}, signal) {
  const url = `${REGEOCODE_URL}?location=${longitude},${latitude}&coordinateSystem=wgs84`;

  return fetch(url, signal ? {signal} : undefined)
    .then(response => {
      if (!response.ok) {
        throw createLocationError('定位服务暂时不可用', 'reverse_geocode_failed');
      }
      return response.json();
    })
    .then(data => {
      const component = data && data.regeocode && data.regeocode.addressComponent;

      if (!component || data.info !== 'OK') {
        throw createLocationError('未能识别当前位置所在城市', 'reverse_geocode_failed');
      }

      const name = normalizeCityName(component.city || component.province || component.district);
      const id = getGbCityCode(component);

      if (!name) {
        throw createLocationError('未获取到有效城市名称', 'reverse_geocode_failed');
      }

      return {
        acronym: '',
        firstChar: '',
        id,
        gbCityCode: id,
        name,
        pinyin: '',
        rank: '',
        latitude,
        longitude,
        coordinateSystem: 'wgs84',
        accuracyMeters: metadata.accuracyMeters ?? null,
        timestampMs: metadata.timestampMs ?? null,
        districtName: String(component.district || '').trim(),
        adcode: String(component.adcode || '').trim(),
        cityResolution: 'resolved',
        cityResolutionReason: ''
      };
    });
}

function normalizePosition (position) {
  const coords = position && position.coords;
  const longitude = coords && coords.longitude;
  const latitude = coords && coords.latitude;
  const accuracy = Number(coords && coords.accuracy);
  const timestamp = Number(position && position.timestamp);

  if (!isValidCoordinate(longitude) || !isValidCoordinate(latitude)) {
    throw createLocationError('未获取到有效位置信息', 'invalid_position');
  }

  return {
    accuracyMeters: Number.isFinite(accuracy) && accuracy >= 0 ? accuracy : null,
    latitude,
    longitude,
    timestampMs: Number.isFinite(timestamp) && timestamp >= 0 ? timestamp : Date.now()
  };
}

export function requestCurrentLocation () {
  if (!navigator.geolocation) {
    return Promise.reject(createLocationError('当前浏览器不支持定位', 'unsupported'));
  }

  if (window.isSecureContext === false) {
    return Promise.reject(createLocationError('定位需要通过 HTTPS 页面使用', 'insecure_context'));
  }

  return new Promise((resolve, reject) => {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    let settled = false;
    let stage = 'position';
    let deadlineTimer = null;
    let normalizedPosition = null;

    const finish = (handler, value) => {
      if (settled) {
        return;
      }
      settled = true;
      if (deadlineTimer !== null) {
        window.clearTimeout(deadlineTimer);
      }
      if (handler === reject && controller) {
        controller.abort();
      }
      handler(value);
    };

    deadlineTimer = window.setTimeout(() => {
      const reverseGeocodePending = stage === 'reverse_geocode';
      if (reverseGeocodePending && normalizedPosition) {
        if (controller) {
          controller.abort();
        }
        finish(resolve, createCoordinateOnlyLocation(
          normalizedPosition,
          'reverse_geocode_deadline'
        ));
        return;
      }
      finish(reject, createLocationError(
        '定位请求超时',
        'location_deadline',
        3
      ));
    }, LOCATION_REQUEST_DEADLINE_MS);

    try {
      navigator.geolocation.getCurrentPosition(
        position => {
          if (settled) {
            return;
          }
          let normalized;
          try {
            normalized = normalizePosition(position);
            normalizedPosition = normalized;
          } catch (error) {
            finish(reject, error);
            return;
          }

          stage = 'reverse_geocode';
          reverseGeocode(
            normalized.longitude,
            normalized.latitude,
            {
              accuracyMeters: normalized.accuracyMeters,
              timestampMs: normalized.timestampMs
            },
            controller ? controller.signal : undefined
          )
            .then(city => finish(resolve, city))
            .catch(error => finish(resolve, createCoordinateOnlyLocation(
              normalized,
              String(error && error.locationReason || 'reverse_geocode_failed')
            )));
        },
        error => finish(reject, error),
        GEOLOCATION_OPTIONS
      );
    } catch (error) {
      finish(reject, error);
    }
  });
}

function getErrorCode (error) {
  return Number(error && typeof error.code !== 'undefined' ? error.code : 0);
}

export function getLocationRetryActionText () {
  return isIOSDevice() ? '已开启，重新定位' : '重新定位';
}

export function getLocationErrorMessage (error) {
  if (!error) {
    return '未获取到位置信息，请确认手机定位已开启后重试';
  }

  const reason = String(error.locationReason || '');
  if (reason === 'reverse_geocode_deadline') {
    return '已获取当前位置，但定位服务响应超时，请稍后重试';
  }

  const code = getErrorCode(error);
  if (isIOSDevice() && (reason === 'location_deadline' || [1, 2, 3].includes(code))) {
    return IOS_LOCATION_GUIDANCE;
  }

  if (reason === 'location_deadline') {
    return '定位请求超时，请确认手机系统定位已开启，并允许浏览器访问位置后重试';
  }

  switch (code) {
    case 1:
      return '请允许浏览器访问位置。如果已拒绝，请在浏览器设置中开启本网站定位权限';
    case 2:
      return '暂时无法获取当前位置，请确认手机系统定位服务已开启后重试';
    case 3:
      return '定位超时，请确认手机系统定位已开启，并在网络良好时重试';
    default:
      return error.message || '未获取到位置信息，请确认手机定位已开启后重试';
  }
}
